import { useTranslation } from 'react-i18next';
import { Campo, Selector } from './ui/formulario';

/** Un país como lo ofrece `GET /empresas/opciones`: con lo que su paquete admite (E35). */
export interface PaisDelAlta {
  readonly codigo: string;
  readonly claveDeTraduccion: string;
  readonly monedaPorDefecto: string;
  readonly tiposDeIdentificacion: readonly string[];
  readonly ejemploDeIdentificacion: string;
  readonly condiciones: readonly string[];
  readonly jurisdicciones: readonly string[];
}

export interface DatosFiscales {
  readonly paisCodigo: string;
  readonly tipoDeIdentificacion: string;
  readonly identificacion: string;
  readonly condicionIva: string;
  readonly jurisdiccionCodigo: string;
  readonly monedaBase: string;
}

/** Con qué arranca el formulario: Argentina, que es donde opera casi todo el que lo abre hoy. */
export const DATOS_FISCALES_INICIALES: DatosFiscales = {
  paisCodigo: 'AR',
  tipoDeIdentificacion: 'CUIT',
  identificacion: '',
  condicionIva: 'responsable_inscripto',
  jurisdiccionCodigo: '',
  monedaBase: 'ARS',
};

/**
 * Lo que cambia al elegir otro país: la clave, la condición, la región y la moneda vuelven a las
 * del país. Lo tipeado en el número se conserva —puede ser que sólo se eligió mal el país—, y el
 * servidor lo valida igual con el paquete que corresponde.
 */
function alCambiarDePais(pais: PaisDelAlta, previo: DatosFiscales): DatosFiscales {
  return {
    ...previo,
    paisCodigo: pais.codigo,
    tipoDeIdentificacion: pais.tiposDeIdentificacion[0] ?? '',
    condicionIva: pais.condiciones[0] ?? '',
    jurisdiccionCodigo: '',
    monedaBase: pais.monedaPorDefecto,
  };
}

/**
 * Los datos fiscales del comercio que se da de alta, **por país** (E35).
 *
 * Lo usan el alta guiada y la de la plataforma, que hasta acá tenían cada una su lista argentina
 * escrita a mano. El país va primero porque decide todo lo demás: si la clave se llama CUIT, RUT
 * o RUC, qué condiciones existen, qué regiones y en qué moneda opera.
 */
export function DatosFiscalesDelComercio({
  paises,
  valor,
  alCambiar,
}: {
  readonly paises: readonly PaisDelAlta[];
  readonly valor: DatosFiscales;
  readonly alCambiar: (datos: DatosFiscales) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const pais = paises.find((posible) => posible.codigo === valor.paisCodigo);

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Selector
          etiqueta={t('alta.pais')}
          value={valor.paisCodigo}
          onChange={(evento) => {
            const elegido = paises.find((posible) => posible.codigo === evento.target.value);
            if (elegido !== undefined) alCambiar(alCambiarDePais(elegido, valor));
          }}
        >
          {paises.map((posible) => (
            <option key={posible.codigo} value={posible.codigo}>
              {t(posible.claveDeTraduccion, { defaultValue: posible.codigo })}
            </option>
          ))}
        </Selector>
        <Selector
          etiqueta={t('alta.jurisdiccion')}
          value={valor.jurisdiccionCodigo}
          onChange={(evento) => {
            alCambiar({ ...valor, jurisdiccionCodigo: evento.target.value });
          }}
        >
          <option value="">{t('alta.sinJurisdiccion')}</option>
          {(pais?.jurisdicciones ?? []).map((codigo) => (
            <option key={codigo} value={codigo}>
              {t(`jurisdicciones.${codigo}`, codigo)}
            </option>
          ))}
        </Selector>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {/* Con un solo tipo —el RUT de una empresa chilena— no hay nada que elegir. */}
        {(pais?.tiposDeIdentificacion.length ?? 0) > 1 ? (
          <Selector
            etiqueta={t('alta.tipoDeIdentificacion')}
            value={valor.tipoDeIdentificacion}
            onChange={(evento) => {
              alCambiar({ ...valor, tipoDeIdentificacion: evento.target.value });
            }}
          >
            {(pais?.tiposDeIdentificacion ?? []).map((tipo) => (
              <option key={tipo} value={tipo}>
                {t(`identificaciones.${tipo}`, tipo)}
              </option>
            ))}
          </Selector>
        ) : null}
        <Campo
          etiqueta={t(`identificaciones.${valor.tipoDeIdentificacion}`, valor.tipoDeIdentificacion)}
          obligatorio
          value={valor.identificacion}
          placeholder={pais?.ejemploDeIdentificacion}
          ayuda={t('alta.ayudaIdentificacion', { ejemplo: pais?.ejemploDeIdentificacion ?? '' })}
          onChange={(evento) => {
            alCambiar({ ...valor, identificacion: evento.target.value });
          }}
        />
        <Selector
          etiqueta={t('alta.condicionIva')}
          value={valor.condicionIva}
          onChange={(evento) => {
            alCambiar({ ...valor, condicionIva: evento.target.value });
          }}
        >
          {(pais?.condiciones ?? []).map((condicion) => (
            <option key={condicion} value={condicion}>
              {t(`condicionesFiscales.${valor.paisCodigo}.${condicion}`, condicion)}
            </option>
          ))}
        </Selector>
      </div>

      <p className="text-xs text-texto-tenue">
        {t('alta.monedaDelPais', { moneda: valor.monedaBase })}
      </p>
    </>
  );
}
